import {
  baseEmotionsEn,
  baseEmotionsRu,
  baseFeelingsEn,
  baseFeelingsRu,
} from '@cc/schema';
import {
  behaviorExperimentObservationCountOnRead,
  behaviorExperimentObservationsOnRead,
  behaviorExperimentStatusOnRead,
  isLegacyOutcomeObservation as isLegacyOutcomeObservationOfUtils,
  legacyOutcomeHasObservationContent as legacyOutcomeHasObservationContentOfUtils,
  legacyReflectionPrefill as legacyReflectionPrefillOfUtils,
  sortBehaviorExperimentObservations,
  type BehaviorExperimentObservationOrder,
  type DerivedLegacyObservation,
} from '@cc/utils';
import type {
  BehaviorExperiment,
  BehaviorExperimentObservation,
  BehaviorExperimentSuggestions,
  CompleteExperimentInput,
  DesignBehaviorExperimentInput,
  EmotionInput,
  RecordObservationInput,
} from '../../schema/be-graphql-generated';

/**
 * E02 · the portal's PURE helpers for the E-era behavior experiment: what the read model carries
 * additively on the legacy row (handoff §4), and how the shipped form's values become the ES
 * commands. No I/O, no React — the view, the editor and the list all read the row through these.
 *
 * The CONVERTED-row rules — always `active`, the single legacy `outcome` read as the FIRST
 * read-only observation, its reflection leaves a completion PRE-FILL — live in ONE place for both
 * surfaces (`@cc/utils` `behaviorExperimentDerive`, the read-side twin of the domain fold; founder
 * ruling 2026-09-02). This module re-exports them in the shapes these screens want.
 */

/**
 * RE-EXPORTED AS BINDINGS, not with `export { … }` — and that is a webpack requirement, not a style
 * choice. `@cc/utils` is built to CommonJS, so its named exports reach here through `__exportStar`,
 * which webpack cannot follow statically: the pass-through form made every build print *"export
 * 'isLegacyOutcomeObservation' was not found in './esHelpers'"* and put that error on the dev
 * server's overlay, even though CJS interop resolved it fine at runtime. Assigning them to consts
 * gives webpack something it can see.
 */
export const isLegacyOutcomeObservation = isLegacyOutcomeObservationOfUtils;
export const legacyOutcomeHasObservationContent =
  legacyOutcomeHasObservationContentOfUtils;
export const legacyReflectionPrefill = legacyReflectionPrefillOfUtils;
export type { DerivedLegacyObservation };

/** The EsDomain member the era gate asks for (plan 07 §2; handoff §2). */
export const BEHAVIOR_EXPERIMENT_DOMAIN = 'behaviorExperiment';

export type ExperimentStatus = 'draft' | 'active' | 'completed';

/** One badge colour per lifecycle state — the list and the view must agree at a glance. */
export const STATUS_TAG_COLOR: Record<ExperimentStatus, string> = {
  draft: 'gold',
  active: 'green',
  completed: 'blue',
};

/** 0 = A, 1 = B, 2 = mixed (registry `whichHypothesisHeld-shape`). */
export const HYPOTHESIS_HELD_VALUES = [0, 1, 2] as const;

/**
 * The two coexistence codes (plan 04 §2.2's symmetric guard) in the THERAPIST's voice. The API
 * localises its own message by `Accept-Language`, but it is written for the ACCOUNT OWNER — "Your
 * account was just updated" — and in the portal the reader is the therapist, not the account. Every
 * other coded rejection still shows the server's own copy verbatim; only these two are re-voiced
 * (founder QA 2026-09-02).
 */
export const COEXISTENCE_COPY_KEYS: Record<string, string> = {
  ES_MODE_MISMATCH: 'pages.behaviorExperiment.esModeMismatch',
  ES_RESEND_AS_ES: 'pages.behaviorExperiment.esResendAsEs',
};

/** Which end of the observations list the therapist wants first — a view preference, never stored. */
export type ObservationOrder = BehaviorExperimentObservationOrder;

// ── the ONE edit gate, shared by the list and the view ───────────────────────────────────────────

/** Everything the edit gate needs to know about a client — as `clients` and `client(id)` return it. */
export type EditGateClient =
  | {
      accessLevel?: string | null;
      access?: {
        allowEdit?: boolean | null;
        allowAdminEdit?: boolean | null;
      } | null;
    }
  | null
  | undefined;

export type ExperimentEditGate = {
  /** show the affordance at all — the therapist holds an edit relation to this client */
  offered: boolean;
  /** …and it can be pressed — the client's account is paid for */
  enabled: boolean;
  /** the tooltip that explains the state above */
  tooltipKey: string;
};

/**
 * May THIS therapist edit THIS client's experiment design?
 *
 * The rule is the shipped L02 one, unchanged — an edit relation (`access.allowEdit`, or
 * `access.allowAdminEdit` while the therapist is a THERAPIST_ADMIN) decides whether the affordance
 * is offered, and the client's `accessLevel` decides whether it is usable; a client without one
 * gets the action DISABLED with the shipped explanation, never silently removed.
 *
 * It lives here because two surfaces ask it: the list's pencil and the view's "Edit design" header
 * button. They used to answer differently — the list gated on the rule, the view offered the button
 * on every row (founder QA 2026-09-02, defect A) — so a therapist saw an edit affordance on a row
 * the list had already told them they could not edit.
 */
export function canEditExperiment(
  client: EditGateClient,
  opts: { isTherapistAdmin?: boolean } = {},
): ExperimentEditGate {
  const offered = Boolean(
    client?.access?.allowEdit ||
      (client?.access?.allowAdminEdit && opts.isTherapistAdmin),
  );
  const enabled = offered && Boolean(client?.accessLevel);
  return {
    offered,
    enabled,
    tooltipKey: enabled
      ? 'resourcesCommon.edit'
      : 'clients.thoughtsTestDisabledTip',
  };
}

/**
 * The observations list in the therapist's chosen order — PURE, client-side, by `occurredAt`, with
 * the recorded order as the tie-break so two runs saved in the same second never swap on a
 * re-render. The DERIVED legacy observation of a converted row participates like any other entry
 * (its `occurredAt` is the row's `updatedAt ?? createdAt` — handoff §4).
 */
export function sortObservations<T extends { occurredAt?: unknown }>(
  observations: readonly T[],
  order: ObservationOrder,
): T[] {
  // ONE copy of the rule, shared with the mobile Detail screen (founder 2026-09-02: "unify")
  return sortBehaviorExperimentObservations(observations, order);
}

/** Drop rows the practice query returned once per permission edge (the L12 finding — E12 list.tsx). */
export function dedupeById<T extends { _id?: string | null }>(
  rows: readonly T[] | null | undefined,
): T[] {
  const seen = new Set<string>();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const id = row?._id;
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

type OutcomeLike = {
  patientReflections?: string | null;
  therapistReflections?: string | null;
  newLearnings?: string | null;
  meaningForOriginalBelief?: string | null;
  newThought?: string | null;
  newThoughtStrength?: number | null;
  oldThoughtStrength?: number | null;
  behaviors?: string | null;
  otherPredictions?: string | null;
  emotions?: ReadonlyArray<EmotionLike | undefined> | null;
  feelings?: ReadonlyArray<number | null | undefined> | null;
} | null;

type RowLike = Pick<
  BehaviorExperiment,
  | 'experimentId'
  | 'status'
  | 'activatedAt'
  | 'completedAt'
  | 'createdAt'
  | 'updatedAt'
  | 'observations'
  | 'followUpExperimentsIdeas'
> & { outcome?: OutcomeLike };

const filled = (s: string | null | undefined): boolean => !!(s ?? '').trim();

/**
 * Does the legacy outcome carry the completion-shaped text a therapist wrote up? It no longer
 * decides the STATUS (a converted row is always `active` — founder ruling 2026-09-02); it decides
 * whether the ES_RESEND_AS_ES fallback has a completion to re-issue.
 */
export const outcomeHasReflection = (
  outcome: OutcomeLike | undefined,
  followUpExperimentsIdeas?: string | null,
): boolean =>
  filled(outcome?.patientReflections) ||
  filled(outcome?.therapistReflections) ||
  filled(outcome?.newLearnings) ||
  filled(outcome?.meaningForOriginalBelief) ||
  filled(outcome?.newThought) ||
  filled(followUpExperimentsIdeas);

/**
 * Status ON READ (handoff §4 as amended by the founder ruling 2026-09-02): the projection's
 * `status` when present; a converted row (identity, no status yet) is **`active`** — never
 * `completed` inferred from its legacy outcome. A row the log does not own has no E-era status.
 */
export function deriveStatus(
  row: RowLike | null | undefined,
): ExperimentStatus | undefined {
  return behaviorExperimentStatusOnRead(row);
}

/**
 * The observations the view shows: the row's own runs, or the single derived legacy one for a
 * converted row that has none. Read-only entries carry `legacy: true` and the reserved id.
 */
export const observationsOnRead = (
  row: RowLike | null | undefined,
): Array<BehaviorExperimentObservation | DerivedLegacyObservation> =>
  behaviorExperimentObservationsOnRead<BehaviorExperimentObservation>(row);

/** The count the list and the view show — the derived legacy observation counts. */
export const observationCount = (row: RowLike | null | undefined): number =>
  behaviorExperimentObservationCountOnRead(row);

/**
 * `durationDays = completedAt − activatedAt` (derived on read; the board's read-only box). An active
 * experiment counts to now; a converted row's activation is its `createdAt` (the only moment the
 * row can vouch for — registry best_call).
 */
export function durationDays(
  row: RowLike | null | undefined,
  now: Date = new Date(),
): number | undefined {
  const start = row?.activatedAt ?? (row?.experimentId ? row?.createdAt : null);
  if (!start) return undefined;
  const end = row?.completedAt ?? now;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (Number.isNaN(ms)) return undefined;
  return Math.max(0, Math.round(ms / 86_400_000));
}

// ── emotion / sensation catalogues (the therapist's pick — ids only travel) ──────────────────────

type Catalogue = ReadonlyArray<{
  name: string;
  children: ReadonlyArray<{ id: number; name: string }>;
}>;

export const emotionCatalogue = (locale?: string | null): Catalogue =>
  (locale ?? '').startsWith('ru') ? baseEmotionsRu : baseEmotionsEn;

export const feelingCatalogue = (locale?: string | null): Catalogue =>
  (locale ?? '').startsWith('ru') ? baseFeelingsRu : baseFeelingsEn;

const nameIn = (catalogue: Catalogue, id: number): string =>
  catalogue.flatMap((g) => g.children).find((c) => c.id === id)?.name ??
  String(id);

export const emotionName = (id: number, locale?: string | null): string =>
  nameIn(emotionCatalogue(locale), id);

export const feelingName = (id: number, locale?: string | null): string =>
  nameIn(feelingCatalogue(locale), id);

// ── form values → commands ──────────────────────────────────────────────────────────────────────

type EmotionLike = { id?: number | null; strength?: number | null } | null;

/** antd's hidden emotion list may hold holes/undefined entries — only real `{id, strength}` pairs travel. */
export const sanitizeEmotions = (
  list: ReadonlyArray<EmotionLike | undefined> | null | undefined,
): EmotionInput[] =>
  (Array.isArray(list) ? list : [])
    .filter(
      (e): e is { id: number; strength?: number | null } =>
        !!e && typeof e.id === 'number',
    )
    .map((e) => ({
      id: e.id,
      strength: typeof e.strength === 'number' ? e.strength : 50,
    }));

export const sanitizeInts = (
  list: ReadonlyArray<number | null | undefined> | null | undefined,
): number[] =>
  (Array.isArray(list) ? list : []).filter(
    (n): n is number => typeof n === 'number',
  );

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/** The shipped form's value shape (create.tsx `initialValues`), loosely typed on purpose. */
export type DesignFormValues = {
  purpose?: number;
  design?: number;
  hypothesisType?: number;
  type?: number;
  activeSubtype?: number | null;
  observationalSubtype?: number | null;
  hypothesisA?: string | null;
  hypothesisAStrength?: number | null;
  hypothesisAConfirmationCriteria?: string | null;
  hypothesisB?: string | null;
  hypothesisBStrength?: number | null;
  hypothesisBConfirmationCriteria?: string | null;
  settings?: {
    experiment?: string | null;
    time?: string | null;
    place?: string | null;
    people?: string | null;
    resources?: string | null;
    survey?: string | null;
  } | null;
  prediction?: {
    emotions?: ReadonlyArray<EmotionLike | undefined> | null;
    feelings?: ReadonlyArray<number | null | undefined> | null;
    behaviors?: string | null;
    otherPredictions?: string | null;
  } | null;
  safetyBehaviors?: string | null;
  helpfulCopingStrategies?: string | null;
  outcome?: {
    behaviors?: string | null;
    emotions?: ReadonlyArray<EmotionLike | undefined> | null;
    feelings?: ReadonlyArray<number | null | undefined> | null;
    patientReflections?: string | null;
    therapistReflections?: string | null;
    newLearnings?: string | null;
    meaningForOriginalBelief?: string | null;
    oldThoughtStrength?: number | null;
    newThought?: string | null;
    newThoughtStrength?: number | null;
  } | null;
  followUpExperimentsIdeas?: string | null;
};

/**
 * ONE `designBehaviorExperiment` from the whole form (portal-per-step resolved_v2 (c)).
 *
 * NEW (`experimentId` null): the setup enums travel (the server mints the id) and the design is
 * activated on save — the portal design is complete when the therapist saves it, as the legacy form
 * was. EDIT: the setup is NOT sent (the selectors are disabled — SETUP_IMMUTABLE otherwise) and the
 * three sections travel even when blank, so only the changed ones are recorded server-side (T102)
 * and the design still counts as complete for activation.
 */
export function designInputFromForm(
  values: DesignFormValues,
  opts: { subject: string; experimentId: string | null; activate?: boolean },
): DesignBehaviorExperimentInput {
  const setup: Partial<DesignBehaviorExperimentInput> = opts.experimentId
    ? {}
    : {
        purpose: num(values.purpose),
        design: num(values.design),
        hypothesisType: num(values.hypothesisType),
        type: num(values.type),
        ...(values.type === 0
          ? { activeSubtype: num(values.activeSubtype) }
          : {}),
        ...(values.type === 1
          ? { observationalSubtype: num(values.observationalSubtype) }
          : {}),
      };
  return {
    subject: opts.subject,
    ...(opts.experimentId ? { experimentId: opts.experimentId } : {}),
    ...setup,
    hypotheses: {
      hypothesisA: str(values.hypothesisA),
      hypothesisAStrength: num(values.hypothesisAStrength),
      hypothesisAConfirmationCriteria: str(
        values.hypothesisAConfirmationCriteria,
      ),
      hypothesisB: str(values.hypothesisB),
      hypothesisBStrength: num(values.hypothesisBStrength),
      hypothesisBConfirmationCriteria: str(
        values.hypothesisBConfirmationCriteria,
      ),
    },
    plan: {
      settings: {
        experiment: str(values.settings?.experiment),
        time: str(values.settings?.time),
        place: str(values.settings?.place),
        people: str(values.settings?.people),
        resources: str(values.settings?.resources),
        survey: str(values.settings?.survey),
      },
      safetyBehaviors: str(values.safetyBehaviors),
      helpfulCopingStrategies: str(values.helpfulCopingStrategies),
    },
    prediction: {
      emotions: sanitizeEmotions(values.prediction?.emotions),
      feelings: sanitizeInts(values.prediction?.feelings),
      behaviors: str(values.prediction?.behaviors),
      otherPredictions: str(values.prediction?.otherPredictions),
    },
    activate: opts.activate ?? true,
  };
}

/**
 * T112 mirror: a legacy save refused with ES_RESEND_AS_ES is re-issued as the design command PLUS
 * `completeExperiment` when the legacy outcome section carries reflection text. The outcome's
 * run-like fields (behaviors / emotions / sensations) are NOT re-issued — they are observations in
 * the E-era and the therapist records them from the experiment view (registry best_call).
 */
export function completionInputFromLegacyOutcome(
  values: DesignFormValues,
  opts: { subject: string; experimentId: string },
): CompleteExperimentInput | null {
  const o = values.outcome;
  if (!outcomeHasReflection(o ?? null, values.followUpExperimentsIdeas)) {
    return null;
  }
  return {
    subject: opts.subject,
    experimentId: opts.experimentId,
    patientReflections: str(o?.patientReflections),
    therapistReflections: str(o?.therapistReflections),
    newLearnings: str(o?.newLearnings),
    meaningForOriginalBelief: str(o?.meaningForOriginalBelief),
    oldThoughtStrength: num(o?.oldThoughtStrength),
    newThought: str(o?.newThought),
    newThoughtStrength: num(o?.newThoughtStrength),
    followUpExperimentsIdeas: str(values.followUpExperimentsIdeas),
  };
}

/**
 * The AI answer (GptBehaviorExperiment read = the suggestions payload) in the form's shape, so the
 * shipped variant machinery (`resetValuesFromVariant` — typed in, switchable, removable) applies it
 * exactly as it applied the legacy model's answer. Strings only: null leaves become '' (the typing
 * mimic recurses into objects and would choke on null); `prediction.emotions/feelings` stay the
 * therapist's pick (always empty from the model — handoff §3).
 */
export function suggestionsToVariant(
  s: BehaviorExperimentSuggestions,
): DesignFormValues {
  return {
    purpose: s.purpose ?? undefined,
    design: s.design ?? undefined,
    hypothesisType: s.hypothesisType ?? undefined,
    type: s.type ?? undefined,
    activeSubtype: s.activeSubtype ?? undefined,
    observationalSubtype: s.observationalSubtype ?? undefined,
    hypothesisA: str(s.hypothesisA),
    hypothesisAStrength: s.hypothesisAStrength ?? 50,
    hypothesisAConfirmationCriteria: str(s.hypothesisAConfirmationCriteria),
    hypothesisB: str(s.hypothesisB),
    hypothesisBStrength: s.hypothesisBStrength ?? 50,
    hypothesisBConfirmationCriteria: str(s.hypothesisBConfirmationCriteria),
    settings: {
      experiment: str(s.settings?.experiment),
      time: str(s.settings?.time),
      place: str(s.settings?.place),
      people: str(s.settings?.people),
      resources: str(s.settings?.resources),
      survey: str(s.settings?.survey),
    },
    prediction: {
      emotions: [],
      feelings: [],
      behaviors: str(s.prediction?.behaviors),
      otherPredictions: str(s.prediction?.otherPredictions),
    },
    safetyBehaviors: str(s.safetyBehaviors),
    helpfulCopingStrategies: str(s.helpfulCopingStrategies),
    followUpExperimentsIdeas: str(s.followUpExperimentsIdeas),
  };
}

/** The observation drawer's values → the RecordObservation / UpdateObservation fields. */
export type ObservationFormValues = {
  whatHappened?: string | null;
  behaviors?: string | null;
  emotionIds?: number[];
  emotionStrengths?: Record<string, number | null | undefined> | null;
  feelings?: number[];
  whichHypothesisHeld?: number | null;
  rating?: number | null;
  learnings?: string | null;
};

export type ObservationFields = Omit<
  RecordObservationInput,
  'subject' | 'experimentId'
>;

export function observationInputFromForm(
  values: ObservationFormValues,
): ObservationFields {
  const ids = sanitizeInts(values.emotionIds);
  return {
    whatHappened: str(values.whatHappened),
    behaviors: str(values.behaviors),
    emotions: ids.map((id) => ({
      id,
      strength: num(values.emotionStrengths?.[String(id)]) ?? 50,
    })),
    feelings: sanitizeInts(values.feelings),
    whichHypothesisHeld: num(values.whichHypothesisHeld),
    rating: num(values.rating),
    learnings: str(values.learnings),
  };
}

/** A recorded run back into the drawer's values (edit-loaded, D-v2-editable-observations). */
export function observationToFormValues(
  o:
    | {
        whatHappened?: string | null;
        behaviors?: string | null;
        emotions?: ReadonlyArray<EmotionLike | undefined> | null;
        feelings?: ReadonlyArray<number | null | undefined> | null;
        whichHypothesisHeld?: number | null;
        rating?: number | null;
        learnings?: string | null;
      }
    | null
    | undefined,
): ObservationFormValues {
  const emotions = sanitizeEmotions(o?.emotions);
  return {
    whatHappened: o?.whatHappened ?? '',
    behaviors: o?.behaviors ?? '',
    emotionIds: emotions.map((e) => e.id),
    emotionStrengths: Object.fromEntries(
      emotions.map((e) => [String(e.id), e.strength]),
    ),
    feelings: sanitizeInts(o?.feelings),
    whichHypothesisHeld: o?.whichHypothesisHeld ?? null,
    rating: o?.rating ?? null,
    learnings: o?.learnings ?? '',
  };
}

/** What the list and the view call an experiment: its tested thought, else its plan, else nothing. */
export const experimentTitle = (
  row:
    | (Pick<BehaviorExperiment, 'hypothesisA' | 'hypothesisB'> & {
        settings?: { experiment?: string | null } | null;
      })
    | null
    | undefined,
): string =>
  (row?.hypothesisA ?? '').trim() ||
  (row?.hypothesisB ?? '').trim() ||
  (row?.settings?.experiment ?? '').trim();

/** The bare legacy `_key` of a `behaviorExperiments/<key>` id — for data-testids. */
export const rowKeyOf = (id: string | null | undefined): string =>
  (id ?? '').split('/').pop() ?? '';
